import * as React from "react";
import { cn } from "@/lib/utils";
import { mushafTextClass } from "@/utils";

interface QuranPageSkeletonProps {
  pageNumber: number;
}

/**
 * The page frame of QuranPageReadingView with pulsing line placeholders, so
 * nothing jumps when the page font lands. Fades in after 150ms (fast loads
 * never flash it); the pulse is opacity-only.
 */
const QuranPageSkeleton: React.FC<QuranPageSkeletonProps> = ({ pageNumber }) => {
  const opening = pageNumber <= 2;

  return (
    <article
      aria-busy="true"
      aria-label="Učitavanje stranice"
      className="mushaf-page relative mx-auto w-fit animate-skeleton-in bg-paper-100 md:rounded-[6px] md:px-10 md:pb-7 md:pt-9 md:shadow-page md:ring-1 md:ring-paper-300"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-2.5 hidden rounded-[3px] ring-1 ring-gold-500/35 md:block"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-3.5 hidden rounded-[2px] ring-1 ring-gold-500/15 md:block"
      />
      <div
        aria-hidden
        className={`${mushafTextClass(pageNumber)} motion-safe:animate-pulse`}
      >
        {/* Pages 1-2 always open with a (compact) surah header, which sets their width. */}
        {opening && <div className="mx-auto h-[1.3em] w-[10.5em] rounded-[3px] bg-paper-300/70" />}
        {Array.from({ length: opening ? 7 : 15 }, (_, i) => (
          <div key={i} className="flex h-[1.4em] items-center justify-center">
            <div
              className={cn(
                "h-[0.45em] rounded-full bg-paper-300/70",
                opening ? "w-[9.4em]" : "w-[14.6em]",
              )}
            />
          </div>
        ))}
      </div>
      <div aria-hidden className="mt-6 flex h-5 items-center justify-center gap-3">
        <span className="h-px w-10 bg-gradient-to-r from-transparent to-gold-500/60" />
        <span className="size-1.5 rounded-full bg-paper-300" />
        <span className="h-px w-10 bg-gradient-to-l from-transparent to-gold-500/60" />
      </div>
      <div className="mushaf-page-shade" aria-hidden />
    </article>
  );
};

export default React.memo(QuranPageSkeleton);
